<template>
  <VaModal v-model="visible" size="small" close-button hide-default-actions @beforeClose="resetForm">
    <h1 class="va-h5">{{ expense?.id ? 'Editar egreso' : 'Registrar nuevo egreso' }}</h1>
    <VaForm v-slot="{ isValid }" ref="expense-form" class="flex-col justify-start items-start gap-4 inline-flex w-full">
      <div class="self-stretch flex-col justify-start items-start gap-4 flex">
        <VaTextarea
          v-model="formData.purchaseList[0].detail"
          placeholder="Describa el detalle de su egreso."
          :label="$t('compras.whatBuy')"
          class="w-full required-field"
          :rules="[validators.required]"
        />
        <VaDateInput
          v-model="formData.date"
          :label="$t('compras.when')"
          class="w-full required-field"
          :rules="[validators.required]"
          :format="formatDate"
        />
        <VaInput
          v-model="formData.total"
          placeholder="Ingrese el valor del egreso."
          type="number"
          :label="$t('compras.howMuch')"
          class="w-full required-field"
          :rules="[validators.required]"
          @focus="formData.total ||= null"
          @blur="formData.total ??= 0"
        />
        <VaSelect
          v-model="formData.paymentMethodId"
          placeholder="Seleccione método de pago."
          :options="paymentMethods"
          text-by="name"
          option-by="value"
          value-by="value"
          class="w-full required-field"
          :label="$t('compras.paymentMethodUse')"
          :rules="[validators.required]"
        />
        <VaSelect
          v-if="assignmentList.length"
          v-model="assignment"
          :label="assignmentLabel"
          :placeholder="assignmentPlaceholder"
          :options="assignmentList"
          text-by="label"
          value-by="id"
          clearable
          class="w-full"
        />
        <VaSelect
          v-if="spendFund && spendFund.items.length"
          v-model="fundItemId"
          label="Compra proyectada"
          placeholder="Gasto no proyectado"
          :options="purchaseOptions"
          text-by="detail"
          value-by="id"
          clearable
          class="w-full"
        />
        <p v-if="hasFundOptions && !userStore.isPersonalWorkspace" class="mb-0 text-xs text-secondary">
          La categoría dice qué compraste. El fondo dice con qué dinero lo pagaste.
        </p>
        <VaSelect
          v-if="!userStore.isPersonalWorkspace && categories.length"
          v-model="category"
          label="Categoría"
          placeholder="Opcional. Elige la categoría del egreso."
          :options="categoryOptions"
          text-by="label"
          value-by="id"
          clearable
          class="w-full"
        />
        <VaSelect
          v-if="accounts.length"
          v-model="formData.accountId"
          placeholder="Opcional."
          :options="accountOptions"
          text-by="name"
          value-by="id"
          clearable
          class="w-full"
          label="Cuenta o banco"
        />
        <div class="flex gap-2 flex-col-reverse w-full sm:flex-row justify-end">
          <VaButton
            preset="info"
            color="transparent"
            class="btn-cancel"
            icon="close"
            :loading="isLoading"
            @click="visible = false"
            >{{ $t('form.labels.cancel') }}</VaButton
          >
          <VaButton
            color="transparent"
            class="btn-save"
            icon="save"
            :disabled="!isValid"
            :loading="isLoading"
            @click="save"
            >{{ $t('form.labels.save') }}</VaButton
          >
        </div>
      </div>
    </VaForm>
  </VaModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useForm, useModal, useToast } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import type { Purchase } from '../../../models/purchase'
import { validators } from '../../../services/utils'
import { usePurchases } from '../../purchase/composables/usePurchases'
import { getAllAccounts } from '../../../services/account'
import { getAllBudgets } from '../../../services/budget'
import { getAllFunds } from '../../../services/fund'
import { getCategories } from '../../../services/invoice'
import type { Account } from '../../../types/account'
import type { Budget } from '../../../types/budget'
import type { Fund } from '../../../types/fund'
import { canRepay, canSpend } from '../../funds/fundUtils'
import {
  assignmentFromDetail,
  assignmentOptions,
  budgetAssignmentId,
  gastoFondoLabel,
  pagoCuotaLabel,
  parseBudgetItemId,
  parseRepayFundId,
  parseSpendFundId,
  repayFundAssignmentId,
  spendFundAssignmentId,
} from '../assignmentOptions'
import { useUserStore } from '../../../stores/user'
import type { ChatFlowDraft } from '../../../types/chat'

const props = defineProps<{ modelValue: boolean; expense?: Purchase | null; draft?: ChatFlowDraft | null }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()
const visible = ref(false)
const { t } = useI18n()
const { confirm } = useModal()
const { init: notify } = useToast()
const form = useForm('expense-form')
const { isLoading, paymentMethods, ...expenseApi } = usePurchases()
const userStore = useUserStore()
const accounts = ref<Account[]>([])
const budgets = ref<Budget[]>([])
const funds = ref<Fund[]>([])
const fundItemId = ref<number | null>(null)
const hydrating = ref(false)
const categories = ref<{ id: number; label: string; icon?: string | null }[]>([])
const assignment = ref<string | null>(null)
const category = ref<number | null>(null)
const budgetsReady = ref(false)

function selectIcon(icon?: string | null) {
  const name = String(icon || '').trim()
  if (!name) return undefined
  return name.startsWith('mso-') ? name : `mso-${name}`
}

const categoryOptions = computed(() =>
  categories.value.map((item) => ({ id: item.id, label: item.label, icon: selectIcon(item.icon) })),
)
const accountOptions = computed(() =>
  accounts.value.map((item) => ({ id: item.id, name: item.name, icon: selectIcon(item.icon) })),
)

const spendFundId = computed(() => parseSpendFundId(assignment.value))
const repayFundId = computed(() => parseRepayFundId(assignment.value))
const fundOptions = computed(() => {
  const options: { id: string; label: string }[] = []
  for (const fund of funds.value) {
    if (canSpend(fund) || fund.id === spendFundId.value) {
      options.push({ id: spendFundAssignmentId(fund.id), label: gastoFondoLabel(fund.detail) })
    }
    const linked = Boolean(fund.repayBudgetItemId)
    if (!linked && (canRepay(fund) || fund.id === repayFundId.value)) {
      options.push({
        id: repayFundAssignmentId(fund.id),
        label: pagoCuotaLabel(fund.detail),
      })
    }
  }
  return options
})
const assignmentList = computed(() => {
  const linkedByItem = new Map(
    funds.value
      .filter((fund) => fund.repayBudgetItemId)
      .map((fund) => [fund.repayBudgetItemId as number, fund]),
  )
  const budget = (userStore.isPersonalWorkspace ? assignmentOptions(budgets.value, formData.value.date, 'E') : []).map(
    (item) => {
      const itemId = parseBudgetItemId(item.id)
      const fund = itemId ? linkedByItem.get(itemId) : undefined
      return fund ? { ...item, label: pagoCuotaLabel(fund.detail) } : item
    },
  )
  const seen = new Set(budget.map((item) => item.id))
  const cuota: { id: string; label: string }[] = []
  for (const fund of funds.value) {
    if (!fund.repayBudgetItemId) continue
    const id = budgetAssignmentId(fund.repayBudgetItemId)
    if (seen.has(id)) continue
    if (!canRepay(fund) && assignment.value !== id) continue
    seen.add(id)
    cuota.push({ id, label: pagoCuotaLabel(fund.detail) })
  }
  return [...budget, ...cuota, ...fundOptions.value]
})
const hasFundOptions = computed(() => fundOptions.value.length > 0)
const hasBudgetOptions = computed(() => assignmentList.value.some((item) => item.id.startsWith('b-')))
const assignmentLabel = computed(() => {
  if (hasBudgetOptions.value && hasFundOptions.value) return 'Asignar a'
  if (hasFundOptions.value) return 'Fondo'
  return 'Presupuesto'
})
const assignmentPlaceholder = computed(() => {
  if (hasBudgetOptions.value && hasFundOptions.value) return 'Opcional. Presupuesto o fondo.'
  if (hasFundOptions.value) return 'Opcional. Elige el fondo.'
  return 'Opcional. Asigna a una línea del presupuesto.'
})
const spendFund = computed(() => funds.value.find((fund) => fund.id === spendFundId.value) || null)
const purchaseOptions = computed(() => spendFund.value?.items || [])

watch(spendFundId, (_value, previous) => {
  if (hydrating.value) return
  if (previous) fundItemId.value = null
})

function clearFunds() {
  fundItemId.value = null
}

function applyFundLine(line: any) {
  hydrating.value = true
  const fundId = Number(line?.fundId) || null
  fundItemId.value = Number(line?.fundItemId) || null
  if (line?.fundRole === 'repay' && line?.budgetItemId) {
    assignment.value = budgetAssignmentId(Number(line.budgetItemId))
  } else if (fundId && line?.fundRole === 'repay') {
    assignment.value = repayFundAssignmentId(fundId)
  } else if (fundId) {
    assignment.value = spendFundAssignmentId(fundId)
  } else {
    assignment.value = assignmentFromDetail(line)
  }
  hydrating.value = false
}

const makeExpense = (): any => ({
  id: null,
  secuencial: '0',
  codDoc: '99',
  date: new Date(),
  code: null,
  emisor: '',
  ruc: '',
  purchaseList: [{ detail: '', price: 0, cant: 1, total: 0, discount: 0, category: null }],
  taxDetail: {},
  tax: 0,
  total: 0,
  paymentMethodId: null,
  accountId: null,
  subtotalTarifaCero: 0,
  subtotalTarifaDiferenteCero: 0,
  buyer: '',
  discount: 0,
  additionalInfo: [],
})
const formData = ref<any>(makeExpense())

watch(
  () => props.modelValue,
  (value) => {
    visible.value = value
    if (value) populateForm()
  },
  { immediate: true },
)
watch(visible, (value) => emit('update:modelValue', value))

onMounted(async () => {
  const [, accountsResponse, budgetsResponse, categoriesResponse, fundsResponse] = await Promise.all([
    expenseApi.getPaymentMethods(),
    getAllAccounts().catch(() => ({ data: [] })),
    userStore.isPersonalWorkspace ? getAllBudgets().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    userStore.isPersonalWorkspace ? Promise.resolve({ data: [] }) : getCategories('E').catch(() => ({ data: [] })),
    getAllFunds().catch(() => ({ data: [] })),
  ])
  if (Array.isArray(accountsResponse.data)) {
    accounts.value = accountsResponse.data
  }
  if (Array.isArray(budgetsResponse.data)) {
    budgets.value = budgetsResponse.data
  }
  if (Array.isArray(categoriesResponse.data)) {
    categories.value = categoriesResponse.data
  }
  if (Array.isArray(fundsResponse.data)) {
    funds.value = fundsResponse.data
  }
  budgetsReady.value = true
  if (visible.value && props.draft?.movement === 'expense') applyDraft()
})

watch(accounts, (list) => {
  if (!props.expense && formData.value.accountId == null && list[0]) {
    formData.value.accountId = list[0].id
  }
})

watch(assignmentList, (list) => {
  if (!budgetsReady.value) return
  if (!assignment.value || list.some((item) => item.id === assignment.value)) return
  if (!funds.value.length && (parseSpendFundId(assignment.value) || parseRepayFundId(assignment.value) || parseBudgetItemId(assignment.value))) return
  assignment.value = null
})

function draftDate(value: string) {
  if (!value) return new Date()
  const date = new Date(`${value}T12:00:00`)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

function draftAccountId(raw: string | undefined) {
  const found = accounts.value.find((item) => String(item.id) === String(raw || ''))
  return found?.id ?? accounts.value[0]?.id ?? null
}

function applyDraft() {
  const draft = props.draft
  if (!draft || draft.kind !== 'payment' || draft.movement !== 'expense') return false
  const total = draft.total || 0
  formData.value = {
    ...makeExpense(),
    date: draftDate(draft.date),
    total,
    paymentMethodId: draft.paymentMethodId || null,
    accountId: draftAccountId(draft.accountId),
    purchaseList: [
      {
        ...makeExpense().purchaseList[0],
        detail: draft.detail,
        price: total,
        total,
      },
    ],
  }
  assignment.value = draft.budgetItemId || null
  clearFunds()
  category.value = draft.categoryId
  return true
}

function populateForm() {
  if (!props.expense && applyDraft()) return
  if (!props.expense) {
    formData.value = {
      ...makeExpense(),
      accountId: accounts.value[0]?.id ?? null,
    }
    assignment.value = null
    clearFunds()
    category.value = null
    return
  }
  const line = (props.expense.purchaseList as any[])[0]
  formData.value = {
    ...makeExpense(),
    ...props.expense,
    date: new Date(`${props.expense.date}T23:59:59`),
    additionalInfo: [],
    purchaseList: [
      {
        ...makeExpense().purchaseList[0],
        ...line,
      },
    ],
  }
  applyFundLine(line)
  category.value = line?.category ?? null
}

function resetForm() {
  formData.value = makeExpense()
  assignment.value = null
  clearFunds()
  category.value = null
}

function formatDate(date: any) {
  return date instanceof Date ? date.toLocaleDateString('es-EC') : new Date().toLocaleDateString('es-EC')
}

async function save() {
  if (!form.validate()) return
  const agreed = await confirm({
    message: t('compras.generateComprobante', { name: formData.value.buyer }),
    okText: t('form.labels.yes'),
    cancelText: t('form.labels.no'),
    size: 'small',
  })
  if (!agreed) return

  formData.value.subtotalTarifaCero = formData.value.total
  formData.value.purchaseList[0].price = formData.value.total
  formData.value.purchaseList[0].total = formData.value.total
  formData.value.purchaseList[0].taxTarifa = 0
  formData.value.purchaseList[0].taxValue = 0
  formData.value.purchaseList[0].category = userStore.isPersonalWorkspace ? null : (category.value || null)
  const budgetItemId = userStore.isPersonalWorkspace ? parseBudgetItemId(assignment.value) : null
  const linkedFund = budgetItemId
    ? funds.value.find((fund) => fund.repayBudgetItemId === budgetItemId) || null
    : null
  const fundId = linkedFund?.id || repayFundId.value || spendFundId.value
  formData.value.purchaseList[0].budgetItemId = linkedFund ? budgetItemId : userStore.isPersonalWorkspace && !fundId
    ? budgetItemId
    : null
  formData.value.purchaseList[0].fundId = fundId
  formData.value.purchaseList[0].fundRole = linkedFund || repayFundId.value ? 'repay' : fundId ? 'spend' : null
  formData.value.purchaseList[0].fundItemId = spendFundId.value && !linkedFund ? (fundItemId.value || null) : null
  formData.value.accountId = formData.value.accountId || null
  formData.value.date = formData.value.date.toLocaleDateString('es-EC')
  if (formData.value.id) await expenseApi.update(formData.value)
  else await expenseApi.add([formData.value])
  notify({ message: t('messages.egresoCreated'), color: 'success' })
  visible.value = false
  emit('saved')
}
</script>
